<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import CardComponent from '@/components/cards/CardComponent.vue'
import BaseButton from '@/components/Buttons/BaseButton.vue'
import SelectOptionPrime from '@/components/forms/SelectOption/SelectOptionPrime.vue'
import Badge from 'primevue/badge'
import type { Tender, WorkflowStatus } from '@/types/tender'
import { useAuthStore } from '@/stores/auth'
import { getBadgeStatus } from '@/utils/badgeUtils'
import { notificationService } from '@/services/notificationService'
import TextAreaDefault from '@/components/forms/TextArea/TextAreaDefault.vue'

// Create a type for the extended properties we need
type ExtendedTenderProps = {
  workflowStatus?: WorkflowStatus | null
  wonAt?: string | Date | null
  rejectedAt?: string | Date | null
  contractValue?: number | string | null
  contractStartDate?: string | Date | null
  rejectionComment?: string | null
  priceReviewNotes?: string | null
  criteriaReviewNotes?: string | null
  priceReviewedAt?: string | Date | null
  criteriaReviewedAt?: string | Date | null
  adminReviewNotes?: string | null
  agentSubmissionNotes?: string | null
  agentNotes?: string | null
  assignmentNotes?: string | null
  wonNotes?: string | null
  lostNotes?: string | null
  wonReviewedAt?: string | Date | null
  lostReviewedAt?: string | Date | null
  agentSubmissionDate?: string | Date | null
  reviewedAt?: string | Date | null
  tenderCompletedNotes?: string | null
  underQueryNotes?: string | null
}

// Create a union type that includes all possible properties
type TenderWithExtended = Tender & Partial<ExtendedTenderProps>

const props = defineProps<{
  tender: TenderWithExtended
}>()

const emit = defineEmits<{
  'status-updated': [status: Tender['status']]
  'workflow-updated': [
    status: WorkflowStatus,
    notes?: string,
    reviewData?: {
      priceReviewNotes?: string
      criteriaReviewNotes?: string
      wonNotes?: string
      lostNotes?: string
      tenderCompletedNotes?: string
      underQueryNotes?: string
    }
  ]
  'under-query-updated': [notes: string]
}>()

const authStore = useAuthStore()
const currentUser = computed(() => authStore.user)

const getWorkflowStatus = computed(() => {
  return props.tender.workflowStatus || 'new'
})

// Workflow stages that come before a bid is submitted. Once the deadline has
// passed while a tender is still in one of these stages, bidding is no
// longer possible - the only path forward is marking it Not Bidded.
const PRE_SUBMISSION_STAGES = ['assigned-to-agent', 'in-progress', 'price-review', 'criteria-review', 'ready-to-bid']

const isDeadlineExpired = computed(() => {
  if (!props.tender.deadline) return false
  const deadline = new Date(props.tender.deadline)
  return !isNaN(deadline.getTime()) && deadline.getTime() < Date.now()
})

// When true, the workflow selector should offer only "Not Bidded" - admin
// and agent both lose every other transition once the deadline has expired
// on a tender that never made it to submission.
const isForcedNotBidded = computed(() => {
  if (!currentUser.value) return false
  if (currentUser.value.userType !== 'admin' && currentUser.value.userType !== 'agent') return false
  return isDeadlineExpired.value && PRE_SUBMISSION_STAGES.includes(getWorkflowStatus.value)
})

// Notes fields
const workflowNotes = ref('')
const priceReviewNotes = ref('')
const criteriaReviewNotes = ref('')
const wonNotes = ref('')
const lostNotes = ref('')
const underQueryNotes = ref('')

// Track if we should show the price review note editor
const showPriceReviewNoteEditor = ref(false)

// Track if notes have been submitted
const notesSubmitted = ref(false)

// Track if under-query notes are being updated
const isUpdatingUnderQuery = ref(false)

// Track if we should show the under query notes editor
const showUnderQueryEditor = ref(false)

// Reset all note fields
const resetNoteFields = () => {
  workflowNotes.value = ''
  priceReviewNotes.value = ''
  criteriaReviewNotes.value = ''
  wonNotes.value = ''
  lostNotes.value = ''
  showPriceReviewNoteEditor.value = false
  notesSubmitted.value = false
  isUpdatingUnderQuery.value = false
}

// Initialize underQueryNotes from props
watch(() => props.tender, (newTender) => {
  if (newTender?.underQueryNotes) {
    underQueryNotes.value = newTender.underQueryNotes
  } else {
    underQueryNotes.value = ''
  }

  // Show under query editor if the current status is under-query
  showUnderQueryEditor.value = getWorkflowStatus.value === 'under-query'
}, { immediate: true })

// Check if the current user can edit specific review notes
const canEditPriceReviewNotes = computed(() => {
  if (!props.tender.priceReviewNotes) return true
  return false
})

const canEditCriteriaReviewNotes = computed(() => {
  if (!props.tender.criteriaReviewNotes) return true
  return false
})

// Helper to determine if we should show any textarea for status change
const shouldShowTextArea = computed(() => {
  if (notesSubmitted.value) return false
  if (selectedWorkflowStatus.value === getWorkflowStatus.value) return false
  return true
})

// Helper to determine which specific textarea to show
const showPriceReviewTextArea = computed(() => {
  return shouldShowTextArea.value &&
         selectedWorkflowStatus.value === 'price-review' &&
         canEditPriceReviewNotes.value
})

const showCriteriaReviewTextArea = computed(() => {
  return shouldShowTextArea.value &&
         selectedWorkflowStatus.value === 'criteria-review' &&
         canEditCriteriaReviewNotes.value
})

const showWonNotesTextArea = computed(() => {
  return shouldShowTextArea.value && selectedWorkflowStatus.value === 'won'
})

const showLostNotesTextArea = computed(() => {
  return shouldShowTextArea.value && selectedWorkflowStatus.value === 'lost'
})

const showCompletionNotesTextArea = computed(() => {
  return shouldShowTextArea.value && selectedWorkflowStatus.value === 'completed'
})

const showUnderQueryTextArea = computed(() => {
  // return getWorkflowStatus.value === 'under-query' && canEditUnderQuery.value

    return selectedWorkflowStatus.value === 'under-query' &&
         canEditUnderQuery.value
})

const showGeneralNotesTextArea = computed(() => {
  if (!shouldShowTextArea.value) return false

  const excludedStatuses = [
    'price-review',
    'criteria-review',
    'won',
    'lost',
    'assigned-to-engineer',
    'completed',
    'under-query',
    'not-bidded'
  ]

  return !excludedStatuses.includes(String(selectedWorkflowStatus.value))
})

const showEditPriceReviewTextArea = computed(() => {
  return shouldShowTextArea.value &&
         showPriceReviewNoteEditor.value &&
         canEditPriceReviewNotes.value
})

// Check if user can edit under query notes (only agents)
const canEditUnderQuery = computed(() => {
  return currentUser.value?.userType === 'agent'
})

// Get badge class using the utility function
const getBadgeClass = (status: string | null | undefined) => {
  const badgeType = getBadgeStatus(status)
  switch (badgeType) {
    case 'success':
      return 'bg-green-100 text-green-800'
    case 'warning':
      return 'bg-yellow-100 text-yellow-800'
    case 'danger':
      return 'bg-red-100 text-red-800'
    case 'info':
      return 'bg-blue-100 text-blue-800'
    case 'neutral':
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

// Workflow status options based on user role
const workflowStatusOptions = computed(() => {
  const allWorkflowStatuses = [
    { value: 'new', label: 'New', description: 'Freshly uploaded tender' },
    { value: 'in-progress', label: 'In Progress', description: 'Tender work is in progress' },
    { value: 'assigned-to-agent', label: 'Assigned', description: 'Assigned to agent for execution' },
    { value: 'price-review', label: 'Price Review', description: 'Admin reviewing pricing' },
    { value: 'criteria-review', label: 'Criteria Review', description: 'Admin reviewing eligibility criteria' },
    { value: 'not-fit', label: 'Not Fit', description: 'Tender not suitable for bidding' },
    { value: 'not-bidded', label: 'Not Bidded', description: 'Deadline passed before a bid was submitted' },
    { value: 'ready-to-bid', label: 'Ready to Bid', description: 'Ready for bidding submission' },
    { value: 'submitted', label: 'Submitted', description: 'Tender Bid has been submitted' },
    { value: 'under-query', label: 'Under Query', description: 'Query raised by buyer - needs response' },
    { value: 'won', label: 'Won', description: 'Tender won successfully' },
    { value: 'lost', label: 'Lost', description: 'Tender lost' },
    { value: 'assigned-to-engineer', label: 'Assigned to Engineer', description: 'Assigned to engineer for execution' },
    { value: 'completed', label: 'Completed', description: 'Project completed' }
  ] as const

  if (!currentUser.value) return []

  // Deadline has passed before submission - the only allowed move is Not Bidded
  if (isForcedNotBidded.value) {
    return allWorkflowStatuses.filter(status => status.value === 'not-bidded')
  }

  const currentStatus = getWorkflowStatus.value

  const adminTransitions: Record<string, string[]> = {
    'assigned-to-agent': ['assigned-to-agent'],
    'in-progress': ['in-progress'],
    'price-review': ['ready-to-bid', 'not-fit'],
    'criteria-review': ['ready-to-bid', 'not-fit'],
    'ready-to-bid': [],
    'won': ['assigned-to-engineer'],
    'lost': [],
    'assigned-to-engineer': ['completed']
  }

  const agentTransitions: Record<string, string[]> = {
    'assigned-to-agent': ['in-progress'],
    'in-progress': ['price-review', 'criteria-review'],
    'price-review': [],
    'criteria-review': [],
    'ready-to-bid': ['submitted'],
    'submitted': ['under-query', 'won', 'lost'],
    'under-query': ['won', 'lost'],
  }

  const engineerTransitions: Record<string, string[]> = {
    'assigned-to-engineer': []
  }

  let allowedTransitions: string[] = []

  switch (currentUser.value.userType) {
    case 'admin':
      allowedTransitions = adminTransitions[currentStatus] || []
      break
    case 'agent':
      allowedTransitions = agentTransitions[currentStatus] || []
      break
    case 'engineer':
      allowedTransitions = engineerTransitions[currentStatus] || []
      break
  }

  allowedTransitions = [currentStatus, ...allowedTransitions]

  return allWorkflowStatuses.filter(status =>
    allowedTransitions.includes(status.value)
  )
})

const selectedWorkflowStatus = ref<WorkflowStatus>(getWorkflowStatus.value)

watch(() => props.tender, (newTender) => {
  if (isForcedNotBidded.value) {
    selectedWorkflowStatus.value = 'not-bidded'
  } else if (newTender?.workflowStatus) {
    selectedWorkflowStatus.value = newTender.workflowStatus
  } else {
    selectedWorkflowStatus.value = 'new'
  }
  resetNoteFields()
}, { immediate: true })

const currentWorkflowStatusBadgeClass = computed(() => {
  return getBadgeClass(getWorkflowStatus.value)
})

const workflowStatusSelectOptions = computed(() =>
  workflowStatusOptions.value.map(status => ({
    label: status.label,
    value: status.value,
    description: status.description
  }))
)

const getStatusLabel = (status: string | null | undefined): string => {
  if (!status) return 'Not Set'

  const statusMap: Record<string, string> = {
    'new': 'New',
    'assigned-to-agent': 'Assigned',
    'in-progress': 'In Progress',
    'price-review': 'Price Review',
    'criteria-review': 'Criteria Review',
    'not-fit': 'Not Fit',
    'not-bidded': 'Not Bidded',
    'ready-to-bid': 'Ready to Bid',
    'submitted': 'Submitted',
    'under-query': 'Under Query',
    'won': 'Won',
    'lost': 'Lost',
    'assigned-to-engineer': 'Assigned to Engineer',
    'completed': 'Completed'
  }
  return statusMap[status] || status
}

watch(selectedWorkflowStatus, (newStatus) => {
  const statusValue = String(newStatus)

  if (statusValue === 'ready-to-bid' && getWorkflowStatus.value === 'price-review') {
    showPriceReviewNoteEditor.value = canEditPriceReviewNotes.value
  } else {
    showPriceReviewNoteEditor.value = false
  }

  if (statusValue === 'won') {
    lostNotes.value = ''
  } else if (statusValue === 'lost') {
    wonNotes.value = ''
  }

  if (statusValue !== getWorkflowStatus.value) {
    notesSubmitted.value = false
  }
})

// Validate all notes based on status
const validateNotes = (): boolean => {
  const statusValue = String(selectedWorkflowStatus.value)

  if (statusValue === 'price-review' && !priceReviewNotes.value.trim()) {
    notificationService.error('Price review notes are required')
    return false
  }

  if (statusValue === 'criteria-review' && !criteriaReviewNotes.value.trim()) {
    notificationService.error('Criteria review notes are required')
    return false
  }

  if (statusValue === 'won' && !wonNotes.value.trim()) {
    notificationService.error('Won notes are required')
    return false
  }

  if (statusValue === 'lost' && !lostNotes.value.trim()) {
    notificationService.error('Lost notes are required')
    return false
  }

  if (statusValue === 'completed' && !workflowNotes.value.trim()) {
    notificationService.error('Completion notes are required')
    return false
  }

  // Under query notes validation - only when changing TO under-query
  if (statusValue === 'under-query' &&
      getWorkflowStatus.value !== 'under-query' &&
      !underQueryNotes.value.trim()) {
    notificationService.error('Please add notes about the query')
    return false
  }

  if (showGeneralNotesTextArea.value && !workflowNotes.value.trim()) {
    const statusLabel = getStatusLabel(statusValue)
    notificationService.error(`${statusLabel} notes are required`)
    return false
  }

  if (showEditPriceReviewTextArea.value && !priceReviewNotes.value.trim()) {
    notificationService.error('Updated price review notes are required')
    return false
  }

  return true
}

const updateWorkflowStatus = () => {
  if (!validateNotes()) {
    return
  }

  const reviewData: {
    priceReviewNotes?: string
    criteriaReviewNotes?: string
    wonNotes?: string
    lostNotes?: string
    tenderCompletedNotes?: string
    underQueryNotes?: string
  } = {}

    const statusValue = String(selectedWorkflowStatus.value)

  // Always include underQueryNotes if they exist (for updates while in same status)
  if (underQueryNotes.value.trim()) {
    reviewData.underQueryNotes = underQueryNotes.value.trim()
  }

  if (statusValue === 'price-review' && priceReviewNotes.value.trim()) {
    reviewData.priceReviewNotes = priceReviewNotes.value.trim()
  }

  if (statusValue === 'criteria-review' && criteriaReviewNotes.value.trim()) {
    reviewData.criteriaReviewNotes = criteriaReviewNotes.value.trim()
  }

  if (statusValue === 'won' && wonNotes.value.trim()) {
    reviewData.wonNotes = wonNotes.value.trim()
  }

  if (statusValue === 'lost' && lostNotes.value.trim()) {
    reviewData.lostNotes = lostNotes.value.trim()
  }

  if (statusValue === 'completed' && workflowNotes.value.trim()) {
    reviewData.tenderCompletedNotes = workflowNotes.value.trim()
  }

  const generalNotes = workflowNotes.value.trim()

  emit('workflow-updated', selectedWorkflowStatus.value, generalNotes, reviewData)

  notesSubmitted.value = true

  // Reset other note fields but KEEP underQueryNotes
  workflowNotes.value = ''
  priceReviewNotes.value = ''
  criteriaReviewNotes.value = ''
  wonNotes.value = ''
  lostNotes.value = ''
  showPriceReviewNoteEditor.value = false
}

// Determine if button should be shown
const shouldShowButton = computed(() => {
  return selectedWorkflowStatus.value !== getWorkflowStatus.value ||
         getWorkflowStatus.value === 'under-query'
})

// Determine button text based on status
const getButtonText = computed(() => {
  if (getWorkflowStatus.value === 'under-query' && selectedWorkflowStatus.value === getWorkflowStatus.value) {
    return 'Save Notes'
  }
  return `Update to ${getStatusLabel(String(selectedWorkflowStatus.value))}`
})
</script>

<template>
  <CardComponent
    title="Tender Status"
    :showHeaderBorder="true"
    :showHeaderPaddingBottom="true"
  >
    <template #body>
      <div class="flex-1 space-y-4">
        <!-- Current Workflow Status Badge -->
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
            Workflow Status
          </span>
          <Badge
            :value="getStatusLabel(getWorkflowStatus)"
            :class="currentWorkflowStatusBadgeClass"
          />
        </div>

        <!-- Workflow Status Selector -->
        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Update Workflow Status
          </label>
          <SelectOptionPrime
            v-model="selectedWorkflowStatus"
            :options="workflowStatusSelectOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Select Workflow Status"
            id="workflow-status"
            :disabled="!currentUser || workflowStatusOptions.length <= 1"
          />
          <p v-if="workflowStatusSelectOptions.find(opt => opt.value === selectedWorkflowStatus)?.description"
             class="text-xs text-gray-500 mt-1">
            {{ workflowStatusSelectOptions.find(opt => opt.value === selectedWorkflowStatus)?.description }}
          </p>
        </div>

        <!-- Under Query Notes - Shows when selected OR current status is under-query -->
        <div v-if="showUnderQueryTextArea" class="space-y-2 pt-2 border-t border-gray-200">
          <TextAreaDefault
            v-model="underQueryNotes"
            id="under-query-notes"
            labelText="Under Query Notes"
            :asterisk="selectedWorkflowStatus === 'under-query' && selectedWorkflowStatus !== getWorkflowStatus"
            :placeholder="selectedWorkflowStatus === 'under-query' && selectedWorkflowStatus !== getWorkflowStatus
              ? 'Add notes about the query... (Required for status change)'
              : 'Add or update notes about the query...'"
            :note="getWorkflowStatus === 'under-query'
              ? 'You can update these notes at any time'
              : 'Required when changing to Under Query status'"
            rows="3"
            size="sm"
          />
        </div>

        <!-- Price Review Notes -->
        <div v-if="showPriceReviewTextArea" class="space-y-2 pt-2 border-t border-gray-200">
          <TextAreaDefault
            v-model="priceReviewNotes"
            id="price-review-notes"
            labelText="Price Review Notes"
            :asterisk="true"
            placeholder="Enter your price review notes here... (Required)"
            :note="'Required for price review. Please provide detailed notes about pricing analysis.'"
            :error-message="String(selectedWorkflowStatus) === 'price-review' && !priceReviewNotes.trim() ? 'Price review notes are required' : ''"
            rows="3"
            size="sm"
          />
        </div>

        <!-- Criteria Review Notes -->
        <div v-if="showCriteriaReviewTextArea" class="space-y-2 pt-2 border-t border-gray-200">
          <TextAreaDefault
            v-model="criteriaReviewNotes"
            id="criteria-review-notes"
            labelText="Criteria Review Notes"
            :asterisk="true"
            placeholder="Enter your criteria review notes here... (Required)"
            :note="'Required for criteria review. Please provide detailed notes about eligibility criteria analysis.'"
            :error-message="String(selectedWorkflowStatus) === 'criteria-review' && !criteriaReviewNotes.trim() ? 'Criteria review notes are required' : ''"
            rows="3"
            size="sm"
          />
        </div>

        <!-- Won Notes -->
        <div v-if="showWonNotesTextArea" class="space-y-2 pt-2 border-t border-gray-200">
          <TextAreaDefault
            v-model="wonNotes"
            id="won-notes"
            labelText="Won Notes"
            :asterisk="true"
            placeholder="Enter won details and notes... (Required)"
            :note="'Required for won status. Please provide details about the win, contract terms, etc.'"
            :error-message="String(selectedWorkflowStatus) === 'won' && !wonNotes.trim() ? 'Won notes are required' : ''"
            rows="3"
            size="sm"
          />
        </div>

        <!-- Lost Notes -->
        <div v-if="showLostNotesTextArea" class="space-y-2 pt-2 border-t border-gray-200">
          <TextAreaDefault
            v-model="lostNotes"
            id="lost-notes"
            labelText="Lost Notes"
            :asterisk="true"
            placeholder="Enter reasons for losing and notes... (Required)"
            :note="'Required for lost status. Please provide reasons for losing and lessons learned.'"
            :error-message="String(selectedWorkflowStatus) === 'lost' && !lostNotes.trim() ? 'Lost notes are required' : ''"
            rows="3"
            size="sm"
          />
        </div>

        <!-- Price Review Note Editor -->
        <div v-if="showEditPriceReviewTextArea" class="space-y-2 pt-2 border-t border-gray-200">
          <TextAreaDefault
            v-model="priceReviewNotes"
            id="edit-price-review-notes"
            labelText="Update Price Review Notes"
            :asterisk="true"
            placeholder="Update price review notes... (Required)"
            :note="'Required before moving to Ready to Bid. Please update the price review notes.'"
            :error-message="String(selectedWorkflowStatus) === 'ready-to-bid' && !priceReviewNotes.trim() ? 'Updated price review notes are required' : ''"
            rows="3"
            size="sm"
          />
        </div>

        <!-- Show message when notes exist but user cannot edit them -->
        <div v-if="showPriceReviewNoteEditor && !canEditPriceReviewNotes" class="space-y-2 pt-2 border-t border-gray-200">
          <div class="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p class="text-sm text-yellow-700">
              Price review notes already exist and cannot be edited.
              The existing notes will be used when moving to "Ready to Bid".
            </p>
          </div>
        </div>

        <!-- General Notes -->
        <div v-if="showGeneralNotesTextArea" class="space-y-2 pt-2 border-t border-gray-200">
          <TextAreaDefault
            v-model="workflowNotes"
            id="workflow-notes"
            labelText="Status Change Notes"
            :asterisk="true"
            :placeholder="`Enter notes for ${getStatusLabel(String(selectedWorkflowStatus))}... (Required)`"
            :note="`Required when changing to ${getStatusLabel(String(selectedWorkflowStatus))}. Please provide details about this status change.`"
            :error-message="showGeneralNotesTextArea && !workflowNotes.trim() ? `${getStatusLabel(String(selectedWorkflowStatus))} notes are required` : ''"
            rows="3"
            size="sm"
          />
        </div>

        <!-- Completion Notes -->
        <div v-if="showCompletionNotesTextArea" class="space-y-2 pt-2 border-t border-gray-200">
          <TextAreaDefault
            v-model="workflowNotes"
            id="completion-notes"
            labelText="Tender Completion Notes"
            :asterisk="true"
            placeholder="Enter completion notes, project summary, final observations... (Required)"
            rows="3"
            size="sm"
            :error-message="String(selectedWorkflowStatus) === 'completed' && !workflowNotes.trim() ? 'Completion notes are required' : ''"
            :note="'Required for completion. These notes will be saved as tender completion notes in the database.'"
          />
        </div>

        <!-- Update Workflow Status Button -->
        <BaseButton
          v-if="shouldShowButton"
          :text="getButtonText"
          variant="primary"
          class="w-full"
          @click="updateWorkflowStatus"
          :disabled="!currentUser"
        />
      </div>
    </template>
  </CardComponent>
</template>
